import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, RefreshCw } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import "./AdminActionCenter.css";

// Everything waiting on the team, most urgent first. Colours: red = urgent
// (money/customers waiting), amber = high (blocking someone), blue = normal
// (requests to decide), green = follow-up (onboarding pipeline).
const SEVERITY = {
  urgent: { label: "Urgent", hint: "Money or customers waiting" },
  high: { label: "High", hint: "Blocking an artist or customer" },
  normal: { label: "Normal", hint: "Requests to decide" },
  followup: { label: "Follow-up", hint: "Onboarding pipeline" },
};

const TAB_LABELS = {
  "payout-console": "Payout console",
  disputes: "Disputes",
  bookings: "Bookings",
  kyc: "KYC queue",
  "reviews-mod": "Review moderation",
  "city-requests": "City requests",
  "category-requests": "Category requests",
  "onboarding-managers": "Onboarding managers",
  "artist-kyc-followups": "KYC follow-ups",
  agreements: "Agreements",
};

const waitingFor = (value) => {
  if (!value) return null;
  const then = new Date(String(value).length === 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(then.getTime())) return null;
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  if (days < 1) return "today";
  return days === 1 ? "1 day" : `${days} days`;
};

export default function AdminActionCenter({ toast, onOpenTab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showClear, setShowClear] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/action-center");
      setData(response.data);
    } catch (error) {
      toast(formatApiError(error), "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { reload(); }, [reload]);

  const buckets = data?.buckets || [];
  const open = buckets.filter((b) => b.count > 0);
  const clear = buckets.filter((b) => b.count === 0);
  const totalOpen = open.reduce((sum, b) => sum + b.count, 0);

  return (
    <div className="action-center" data-testid="admin-action-center">
      <div className="card card-pad action-center-head">
        <div>
          <div className="card-title">Action Center</div>
          <div className="text-muted fs-12">
            {data ? (totalOpen ? `${totalOpen} items waiting on the team, most urgent first.` : "All clear — nothing is waiting right now.") : "Loading…"}
          </div>
        </div>
        <div className="action-center-totals">
          {Object.entries(SEVERITY).map(([key, info]) => (
            <span key={key} className={`action-total sev-${key}`} title={info.hint} data-testid={`action-total-${key}`}>
              <strong>{data?.totals?.[key] ?? "–"}</strong> {info.label}
            </span>
          ))}
          <button type="button" className="btn btn-ghost btn-sm" onClick={reload} disabled={loading}>
            <RefreshCw size={14} aria-hidden="true" /> {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="action-grid">
        {open.map((b) => (
          <section key={b.id} className={`action-card sev-${b.severity}`} data-testid={`action-${b.id}`}>
            <div className="action-card-head">
              <span className={`action-sev sev-${b.severity}`}>{SEVERITY[b.severity].label}</span>
              {b.oldest_at && <span className="action-age">Oldest waiting {waitingFor(b.oldest_at)}</span>}
            </div>
            <div className="action-card-title">
              <span className="action-count">{b.count}</span>
              <h3>{b.title}</h3>
            </div>
            <p className="action-desc">{b.description}</p>
            <ul className="action-items">
              {b.items.map((item, index) => (
                <li key={`${b.id}-${index}`}>
                  {item.link ? <Link to={item.link} className="fw-600">{item.label}</Link> : <span className="fw-600">{item.label}</span>}
                  {item.sub && <span className="action-item-sub">{item.sub}</span>}
                </li>
              ))}
              {b.count > b.items.length && <li className="action-item-more">+{b.count - b.items.length} more</li>}
            </ul>
            <button type="button" className="btn btn-sm action-open" onClick={() => onOpenTab(b.tab)} data-testid={`action-open-${b.id}`}>
              Open {TAB_LABELS[b.tab] || b.tab} <ArrowRight size={14} aria-hidden="true" />
            </button>
          </section>
        ))}
      </div>

      {clear.length > 0 && (
        <div className="card card-pad action-clear">
          <button type="button" className="action-clear-toggle" onClick={() => setShowClear((v) => !v)} aria-expanded={showClear}>
            ✓ {clear.length} {clear.length === 1 ? "queue is" : "queues are"} clear {showClear ? "▴" : "▾"}
          </button>
          {showClear && (
            <div className="action-clear-list">
              {clear.map((b) => <span key={b.id} className={`action-clear-chip sev-${b.severity}`}>{b.title}</span>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
